// @ts-nocheck
import { getFocalPointStyles } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Img, { ImgProps } from "@insite/mobius/Img";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { ToolkitFieldDefinition } from "@toolkit/Framework/Types/FieldDefinition";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    focalPoint = "focalPoint",
    backgroundColor = "backgroundColor",
    textColor = "textColor",
    minimumHeight = "minimumHeight",
    bannerWidth = "bannerWidth",
    fixWidth = "fixWidth",
    imageOverlay = "imageOverlay",
    verticalAligment = "verticalAligment",
    horizontalAligment = "horizontalAligment",
    contentPadding = "contentPadding",
    isPaddingAdvancedOptions = "isPaddingAdvancedOptions",
    paddingTop = "paddingTop",
    paddingRight = "paddingRight",
    paddingBottom = "paddingBottom",
    paddingLeft = "paddingLeft",
    hasBorder = "hasBorder",
    borderColor = "borderColor",
    borderSize = "borderSize",
    borderStyle = "borderStyle",
    borderRadius = "borderRadius",
    logoImage = "logoImage",
    logoMaxWidth = "logoMaxWidth",
    logoMaxHeight = "logoMaxHeight",
    logoPosition = "logoPosition",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.backgroundColor]: string;
        [fields.textColor]: string;
        [fields.minimumHeight]: "None" | "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.bannerWidth]: "fullWidth" | "fitContent" | "fixWidth";
        [fields.fixWidth]: number;
        [fields.imageOverlay]: string;
        [fields.verticalAligment]: "flex-start" | "center" | "flex-end";
        [fields.horizontalAligment]: "flex-start" | "center" | "flex-end";
        [fields.contentPadding]: number;
        [fields.isPaddingAdvancedOptions]: boolean;
        [fields.paddingTop]: number;
        [fields.paddingRight]: number;
        [fields.paddingBottom]: number;
        [fields.paddingLeft]: number;
        [fields.hasBorder]: boolean;
        [fields.borderColor]: string;
        [fields.borderSize]: number;
        [fields.borderStyle]:
            | "dotted"
            | "dashed"
            | "solid"
            | "double"
            | "groove"
            | "ridge"
            | "inset"
            | "outset"
            | "none"
            | "hidden";
        [fields.borderRadius]: number;
        [fields.logoImage]: string;
        [fields.logoMaxWidth]: number;
        [fields.logoMaxHeight]: number;
        [fields.logoPosition]: "top" | "bottom";
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    logo?: ImgProps;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            position: relative;
            display: flex;
            flex-wrap: wrap;
            align-items: stretch;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            text-align: center;
        `,
    },
    logo: {
        css: css`
            position: absolute;
            left: 50%;
            right: 50%;
            transform: translateX(-50%);
        `,
    },
};

export const Banner: FC<OwnProps> = ({ id, fields, extendedStyles }) => {
    const isEditing = useContext(ShellContext).isEditing || false;

    const backgroundStyles =
        fields.background === "image"
            ? `background-image: url(${fields.image});
           background-size: cover;`
            : `background-color: ${fields.backgroundColor};`;

    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    let minimumHeightStyles;
    switch (fields.minimumHeight) {
        case "None":
            minimumHeightStyles = "min-height: none";
            break;
        case "1/4 viewport":
            minimumHeightStyles = "min-height: 25vh;";
            break;
        case "1/2 viewport":
            minimumHeightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            minimumHeightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            minimumHeightStyles = "min-height: 100vh;";
            break;
    }

    let widthStyles;
    if (fields.bannerWidth === "fullWidth") {
        widthStyles = " width: 100%;";
    } else if (fields.bannerWidth === "fitContent") {
        widthStyles = " width: auto;";
    } else if (fields.bannerWidth === "fixWidth") {
        widthStyles = `
            width: 100%;
            max-width: ${pxToRem(fields.fixWidth)};
            max-height: ${!isEditing ? pxToRem(fields.fixWidth) : "none"};
        `;
    }

    const borderSize = fields.borderSize ? fields.borderSize : 1;
    const borderColor = fields.borderColor ? fields.borderColor : "rgba(0,0,0,0)";
    const borderStyles = fields.hasBorder
        ? css`
              border: ${pxToRem(borderSize)} ${fields.borderStyle} ${borderColor};
          `
        : "";

    const borderRadius = css`
        border-radius: ${pxToRem(fields.borderRadius)};
    `;

    const styles = useMergeStyles("banner", bannerStyles, extendedStyles);

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
            ${minimumHeightStyles}
            ${widthStyles}
            ${borderStyles}
            ${borderRadius}
            ${fields.logoImage &&
            `
                margin-top: ${pxToRem(fields.logoMaxHeight / 4)};
            `}
        `,
    };

    const fieldsPadding = fields.isPaddingAdvancedOptions
        ? `${pxToRem(fields.paddingTop)} ${pxToRem(fields.paddingRight)} ${pxToRem(fields.paddingBottom)} ${pxToRem(
              fields.paddingLeft,
          )}`
        : pxToRem(fields.contentPadding);

    const padding =
        fields.logoImage && fields.contentPadding < fields.logoMaxHeight / 2
            ? pxToRem(fields.logoMaxHeight / 2)
            : fieldsPadding;

    const overlayWrapperStyles = {
        css: css`
            ${!isEditing
                ? css`
                      display: flex;
                  `
                : null}
            ${styles.overlayWrapper?.css}
            color: ${fields.textColor};
            padding: ${padding};
            align-items: ${fields.verticalAligment};
            justify-content: ${fields.horizontalAligment};
            ${fields.imageOverlay ? `background-color: ${fields.imageOverlay};` : null}
            ${borderRadius}
        `,
    };

    const slideCenteringWrapperStyles = {
        ...styles.slideCenteringWrapperStyles,
        css: css`
            ${styles.slideCenteringWrapperStyles?.css}
            ${widthStyles}
        `,
    };

    const position = `${fields.logoPosition}: -${pxToRem(fields.logoMaxHeight / 2)}`;

    const logoStyles = {
        css: css`
            ${styles.logo?.css || ""}
            max-height: ${pxToRem(fields.logoMaxHeight)};
            max-width: ${pxToRem(fields.logoMaxWidth)};
            ${position};
        `,
    };

    return (
        <StyledWrapper {...wrapperStyles}>
            {fields.logoImage && <Img loading="lazy" src={fields.logoImage} {...logoStyles} />}
            <StyledWrapper {...overlayWrapperStyles}>
                <StyledWrapper {...slideCenteringWrapperStyles}>
                    <Zone contentId={id} zoneName="containerZone" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const logoTab = {
    displayName: "Icon",
    sortOrder: 2,
};

const banner: WidgetModule<ToolkitFieldDefinition> = {
    component: Banner,
    definition: {
        group: "Basic",
        displayName: "Styled Container",
        icon: "Banner",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "",
                hideEmptyOption: false,
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.imageOverlay,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.textColor,
                editorTemplate: "ColorPickerField",
                displayName: "Text Color",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.verticalAligment,
                displayName: "Content Vertical Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "flex-start" },
                    { displayName: "Middle", value: "center" },
                    { displayName: "Bottom", value: "flex-end" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.horizontalAligment,
                displayName: "Content Horizontal Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Middle", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.contentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                tab: basicTab,
                isVisible: widget => !widget.fields.isPaddingAdvancedOptions,
            },
            {
                fieldType: "General",
                name: fields.isPaddingAdvancedOptions,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.paddingTop,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                tab: basicTab,
                isVisible: widget => widget.fields.isPaddingAdvancedOptions,
            },
            {
                fieldType: "General",
                name: fields.paddingRight,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                tab: basicTab,
                isVisible: widget => widget.fields.isPaddingAdvancedOptions,
            },
            {
                fieldType: "General",
                name: fields.paddingBottom,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                tab: basicTab,
                isVisible: widget => widget.fields.isPaddingAdvancedOptions,
            },
            {
                fieldType: "General",
                name: fields.paddingLeft,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                tab: basicTab,
                isVisible: widget => widget.fields.isPaddingAdvancedOptions,
            },
            {
                fieldType: "General",
                name: fields.minimumHeight,
                displayName: "Minimum Container Height",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "None", value: "none" },
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                fieldType: "General",
                name: fields.bannerWidth,
                displayName: "Container Width",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Full Width", value: "fullWidth" },
                    { displayName: "Fit Content", value: "fitContent" },
                    { displayName: "Fixed Width", value: "fixWidth" },
                ],
                defaultValue: "fullWidth",
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                fieldType: "General",
                name: fields.fixWidth,
                displayName: "Width in pixels",
                editorTemplate: "IntegerField",
                defaultValue: 50,
                tab: settingsTab,
                isVisible: (item: HasFields) => item.fields.bannerWidth === "fixWidth",
            },
            {
                fieldType: "General",
                name: fields.hasBorder,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: settingsTab,
            },
            {
                fieldType: "General",
                name: fields.borderColor,
                displayName: "Border Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "rgba(0,0,0,0)",
                isVisible: widget => widget.fields.hasBorder,
                tab: settingsTab,
            },
            {
                name: fields.borderSize,
                displayName: "Border Size",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                isVisible: widget => widget.fields.hasBorder,
                tab: settingsTab,
            },
            {
                fieldType: "General",
                name: fields.borderStyle,
                displayName: "Border Styles",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "solid", value: "solid" },
                    { displayName: "dotted", value: "dotted" },
                    { displayName: "dashed", value: "dashed" },
                    { displayName: "double", value: "double" },
                    { displayName: "groove", value: "groove" },
                    { displayName: "ridge", value: "ridge" },
                    { displayName: "inset", value: "inset" },
                    { displayName: "outset", value: "outset" },
                    { displayName: "none", value: "none" },
                    { displayName: "hidden", value: "hidden" },
                ],
                defaultValue: "solid",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.hasBorder,
                tab: settingsTab,
            },
            {
                name: fields.borderRadius,
                displayName: "Border Radius",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.logoImage,
                displayName: "Logo Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: logoTab,
            },
            {
                name: fields.logoMaxWidth,
                displayName: "Logo max width",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "Translatable",
                tab: logoTab,
            },
            {
                name: fields.logoMaxHeight,
                displayName: "Logo max height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "Translatable",
                tab: logoTab,
            },
            {
                fieldType: "General",
                name: fields.logoPosition,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "top" },
                    { displayName: "Bottom", value: "bottom" },
                ],
                hideEmptyOption: true,
                defaultValue: "top",
                isVisible: widget => widget.fields.logoImage !== "",
                tab: logoTab,
            },
        ],
    },
};

export default banner;
